import { useEffect } from 'react';
import { Box, Portal } from '@mui/material';

// ── Reference-counting for stacked scroll locks ─────────────────────────────
// When multiple CustomBackdrop instances are open simultaneously (e.g. a main
// dialog and its confirmation dialog), each one increments this counter.  Body
// overflow is only restored when the *last* backdrop unmounts / closes.
let scrollLockCount = 0;
let savedBodyStyles = null;

const lockScroll = (simple) => {
  if (scrollLockCount === 0) {
    // Capture original styles only on the very first lock
    const scrollY = window.scrollY;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    savedBodyStyles = {
      overflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top,
      paddingRight: document.body.style.paddingRight,
      scrollY,
      simple,
    };

    if (simple) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
    }
  }
  scrollLockCount += 1;
};

const unlockScroll = () => {
  scrollLockCount -= 1;
  if (scrollLockCount <= 0) {
    scrollLockCount = 0;
    if (savedBodyStyles) {
      document.body.style.overflow = savedBodyStyles.overflow;
      document.documentElement.style.overflow = savedBodyStyles.htmlOverflow;
      document.body.style.paddingRight = savedBodyStyles.paddingRight;
      document.body.style.position = savedBodyStyles.position;
      document.body.style.width = savedBodyStyles.width;
      document.body.style.top = savedBodyStyles.top;
      if (!savedBodyStyles.simple) {
        window.scrollTo(0, savedBodyStyles.scrollY);
      }
      savedBodyStyles = null;
    }
  }
};

const CustomBackdrop = ({
  open,
  blurIntensity = 3,
  backgroundColor = 'rgba(0, 0, 0, 0.4)',
  zIndex = 10000,
  onClick,
  modalDialogSelector = '[class*="birthday-modal-dialog"]',
  useSimpleScrollLock = false,
}) => {
  useEffect(() => {
    if (open) {
      lockScroll(useSimpleScrollLock);

      const preventInteraction = e => {
        const target = e.target;

        // Check if target is inside a MUI Menu, Popover, DatePicker, or Autocomplete component
        const isInsideOverlay = target.closest(
          [
            '.MuiMenu-root',
            '.MuiPaper-root.MuiMenu-paper',
            '.MuiList-root',
            '.MuiMenuItem-root',
            '.MuiPopover-root',
            '.MuiPopper-root',
            '.MuiPickersPopper-root',
            '.MuiPickersModal-root',
            '.MuiPickersModalDialog-root',
            '.MuiPickersLayout-root',
            '.MuiDateCalendar-root',
            '[class*="MuiPickers"]',
          ].join(', ')
        );
        if (isInsideOverlay) {
          return; // Allow all interactions with menu components
        }

        // Handle multiple selectors by splitting and checking each
        const selectors = modalDialogSelector.split(',').map(s => s.trim());
        let modalElement = null;
        for (const selector of selectors) {
          modalElement = target.closest(selector);
          if (modalElement) break;
        }
        const isClickInsideModal = !!modalElement;

        const backdropElement = target.closest('[data-backdrop="true"]');
        const isTargetBackdrop =
          target.hasAttribute && target.hasAttribute('data-backdrop');
        const isClickOnBackdrop = backdropElement !== null || isTargetBackdrop;

        const isClickEvent = [
          'click',
          'mousedown',
          'mouseup',
          'touchend',
        ].includes(e.type);

        // Allow keyboard events inside modal
        if (e.type === 'keydown' && isClickInsideModal) {
          return;
        }

        if (isClickOnBackdrop && isClickEvent) {
          return;
        }

        if (!isClickInsideModal && !isClickOnBackdrop) {
          // Allow programmatic download links (hidden <a download> elements)
          // created by file-download utilities to trigger the browser save dialog
          const isDownloadAnchor =
            target.tagName === 'A' &&
            target.hasAttribute('download') &&
            target.style.display === 'none';
          if (isDownloadAnchor) return;

          e.stopPropagation();
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }
      };

      const captureEvents = ['touchstart', 'touchmove', 'keydown'];
      const bubbleEvents = ['click', 'mousedown', 'mouseup', 'touchend'];

      captureEvents.forEach(eventType => {
        document.addEventListener(eventType, preventInteraction, true);
      });

      bubbleEvents.forEach(eventType => {
        document.addEventListener(eventType, preventInteraction, false);
      });

      const preventScroll = e => {
        const target = e.target;

        const isInsideOverlay = target.closest(
          [
            '.MuiMenu-root',
            '.MuiPaper-root.MuiMenu-paper',
            '.MuiList-root',
            '.MuiMenuItem-root',
            '.MuiPopover-root',
            '.MuiPopper-root',
            '.MuiPickersPopper-root',
            '.MuiPickersModal-root',
            '.MuiPickersModalDialog-root',
            '.MuiPickersLayout-root',
            '.MuiDateCalendar-root',
            '[class*="MuiPickers"]',
          ].join(', ')
        );
        if (isInsideOverlay) {
          return;
        }

        const selectors = modalDialogSelector.split(',').map(s => s.trim());
        for (const selector of selectors) {
          if (selector && target.closest(selector)) {
            return;
          }
        }

        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('touchmove', preventScroll, { passive: false });

      return () => {
        unlockScroll();

        captureEvents.forEach(eventType => {
          document.removeEventListener(eventType, preventInteraction, true);
        });
        bubbleEvents.forEach(eventType => {
          document.removeEventListener(eventType, preventInteraction, false);
        });
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [open, modalDialogSelector, useSimpleScrollLock]);

  if (!open) return null;

  const handleClick = e => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  const handleInteraction = e => {
    if (e.type === 'wheel' || e.type === 'touchmove') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };

  return (
    <Portal>
      <Box
        data-backdrop="true"
        onClick={handleClick}
        onMouseDown={handleClick}
        onTouchEnd={handleClick}
        onTouchStart={handleInteraction}
        onTouchMove={handleInteraction}
        onWheel={handleInteraction}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor,
          backdropFilter: `blur(${blurIntensity}px)`,
          WebkitBackdropFilter: `blur(${blurIntensity}px)`,
          zIndex,
          pointerEvents: 'auto',
          touchAction: 'pan-y',
          userSelect: 'none',
        }}
      />
    </Portal>
  );
};

export default CustomBackdrop;
